import React, { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import soundSystem from '../utils/sounds';

const SoundSelector = () => {
  const [selectedSound, setSelectedSound] = useState(() => {
    return localStorage.getItem('preferredWelcomeSound') || 'random';
  });
  
  // 🔒 Ref pour éviter les clics multiples rapides
  const isProcessingRef = useRef(false);
  // 🔔 Ref pour le toast ID (éviter les doublons)
  const toastIdRef = useRef(null);

  const soundOptions = [
    { id: 'random', name: '🎲 Surprise !', sound: null },
    { id: 'coin', name: '🪙 Coin', sound: () => soundSystem.playCoin() },
    { id: 'ring', name: '💍 Ring', sound: () => soundSystem.playRing() },
    { id: 'powerup', name: '⚡ Power-Up', sound: () => soundSystem.playPowerUp() },
    { id: 'dog', name: '🐕 Woof', sound: () => soundSystem.playDogBark() },
    { id: 'cat', name: '🐱 Miaou', sound: () => soundSystem.playCatMeow() },
    { id: 'fanfare', name: '🎺 Fanfare', sound: () => soundSystem.playFanfare() },
    { id: 'magic', name: '✨ Magic', sound: () => soundSystem.playMagicSparkle() },
    { id: 'circus', name: '🎪 Ta-daa', sound: () => soundSystem.playCircus() },
    { id: 'rocket', name: '🚀 Décollage', sound: () => soundSystem.playRocketLaunch() },
    { id: 'jackpot', name: '🎰 Jackpot', sound: () => soundSystem.playJackpot() },
    { id: 'victory', name: '🏆 Victory', sound: () => soundSystem.playVictory() },
    { id: 'levelup', name: '⬆️ Level Up', sound: () => soundSystem.playLevelUp() },
    { id: 'airlock', name: '🚪 Sas Spatial', sound: () => soundSystem.playAirlockOpen() },
  ];

  const handleSoundSelect = useCallback((option) => {
    // 🔒 Protection contre les clics multiples rapides
    if (isProcessingRef.current) {
      return;
    }
    
    // Éviter de re-sélectionner le même son
    if (selectedSound === option.id) {
      // Même son sélectionné = juste jouer le son pour écouter
      soundSystem.stopAllSounds();
      if (option.sound) {
        option.sound();
      } else {
        soundSystem.playRandomWelcome();
      }
      return;
    }

    // 🔒 Bloquer les clics pendant 500ms
    isProcessingRef.current = true;
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);
    
    // 🔇 Stopper les sons précédents avant d'en jouer un nouveau
    soundSystem.stopAllSounds();

    // Sauvegarder le choix dans localStorage
    localStorage.setItem('preferredWelcomeSound', option.id);
    setSelectedSound(option.id);
    
    // Jouer le son pour preview
    if (option.sound) {
      option.sound();
    } else {
      // Pour "Surprise", jouer un son aléatoire
      soundSystem.playRandomWelcome();
    }
    
    // 🔔 Fermer l'ancien toast avant d'en afficher un nouveau
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }
    
    // Afficher le toast et stocker son ID
    toastIdRef.current = toast.success(`🔊 Son "${option.name}" sélectionné !`, {
      duration: 2000,
    });
  }, [selectedSound]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {soundOptions.map((option) => {
        const isSelected = selectedSound === option.id;
        
        return (
          <motion.button
            key={option.id}
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSoundSelect(option)}
            className={`p-3 rounded-xl text-sm font-medium transition-all ${
              isSelected
                ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-lg shadow-cyan-500/30'
                : 'bg-black/50 text-gray-300 hover:bg-cyan-500/20 border border-gray-700 hover:border-cyan-500/50'
            }`}
            data-testid={`sound-option-${option.id}`}
          >
            {option.name}
          </motion.button>
        );
      })}
      <p className="col-span-full text-gray-500 text-xs mt-3 text-center">
        💡 Clique sur un son pour l&apos;écouter et le sélectionner
      </p>
    </div>
  );
};

export default SoundSelector;